import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { GraphqlService } from './graphql.service';

// Field names match the external notification server's raw JSON exactly
// (getUserNotifications returns an opaque JSONString, not a typed GraphQL
// object, so there's no camelCase auto-conversion) — see
// store/notifications.py:56-92 and store/graphql/queries/users.py:313-324
// in the backend, and lib/models/notification/app_notification.dart in the
// mobile app, which this mirrors field-for-field.
export interface AppNotification {
  id: number;
  title: string | null;
  body: string | null;
  data_payload?: any;
  image_url?: string | null;
  created_at?: string | null;
  scheduled_at?: string | null;
  sent_at?: string | null;
  status?: string | null;
  priority?: string | null;
  is_silent?: boolean | null;
  click_action?: string | null;
}

const GET_USER_NOTIFICATIONS = gql`
  query GetUserNotifications {
    getUserNotifications
  }
`;

@Injectable({
  providedIn: 'root'
})
export class UserNotificationsService {
  constructor(private apollo: GraphqlService) {}

  // The resolver returns [] (no phone/email on file, or the notification
  // server call failed) or a paginated {next, previous, results} object —
  // two different shapes for "nothing"/"something", normalized here so
  // callers only ever deal with one shape.
  async fetchNotifications(): Promise<{ results: AppNotification[]; hasMore: boolean }> {
    const result: any = await this.apollo.fetchData({
      query: GET_USER_NOTIFICATIONS,
      variables: {},
    });

    const raw = result?.data?.getUserNotifications;
    if (!raw) {
      return { results: [], hasMore: false };
    }

    let parsed: any;
    try {
      parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (error) {
      console.error('Error parsing notifications payload:', error);
      return { results: [], hasMore: false };
    }

    if (Array.isArray(parsed)) {
      return { results: parsed, hasMore: false };
    }

    return {
      results: parsed?.results ?? [],
      hasMore: !!parsed?.next,
    };
  }
}
